<template>
	<div id="app">
		<rysqer-layout> <router-view></router-view></rysqer-layout>
	</div>
</template>

<script setup>
import { ref, reactive, computed, getCurrentInstance, provide } from 'vue'

import RysqerLayout from '@/layout/Layout'

import { useAuthStore } from '@/stores/auth'
import { useAppStore } from '@/stores/apps'
import { useStageStore } from '@/stores/stage'
import { useLibsStore } from '@/stores/libs'
import { useFormattersStore } from '@/stores/formatters'
import { useOptionsStore } from '@/stores/options'
import { useTempStore } from '@/stores/temp'
import { useBpmnStore } from '@/stores/bpmn'
import { useTitle, usePreferredLanguages, useEventBus } from '@vueuse/core'
import { get as _get, set as _set } from 'lodash-es'
import { v4 as uuidv4 } from 'uuid'
import { clickToDownload, b64Decode, b64Encode } from '@/libs/functions'
import { data } from 'vue2-dropzone'
import moment from 'moment'
import axios from '@/libs/axios'
import hash from 'object-hash'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

const preferredLanguages = usePreferredLanguages()
const locale = preferredLanguages.value?.[0] || 'en-US' // Default to en-US

const bus = useEventBus('rysqer-events')

const createEventBus = () => {
	return {
		on(event, callback) {
			return bus.on((payload) => {
				if (payload && payload.event === event) {
					callback(payload.data)
				}
			})
		},
		emit(event, data) {
			bus.emit({ event, data })
		},
		off(event, callback) {
			bus.off(callback)
		},
		reset() {
			bus.reset()
		},
	}
}

const vm = getCurrentInstance()
const router = vm.proxy.$router
const route = computed(() => vm.proxy.$route)

const breakpoints = useBreakpoints(breakpointsTailwind)

const title = useTitle()
title.value = 'Rysqer Tool'

if (useAuthStore().authenticated) {
	useOptionsStore().hydrate()
}

const Rysqer = () => {
	return reactive({
		apps: useAppStore().apps,
		active: useAppStore().active,
		createApp: useAppStore().createApp,
		createView: useAppStore().createView,
		createScene: useAppStore().createScene,
		stages: useStageStore().stages,
		stage: useStageStore(),
		sizes: useStageStore().sizes,
		breakpoints: breakpoints,
		log: console.log,
		router: router,
		route: route,
		libs: {
			vm: vm,
			toast: vm.proxy.$toast,
			dialog: vm.proxy.$dialog,
			uuid: uuidv4,
			log: console.log,
			table: console.table,
			debounce: useLibsStore().debounce,
			moment: moment,
			axios: axios,
			clickToDownload: clickToDownload,
			b64Decode: b64Decode,
			b64Encode: b64Encode,
			getCanvas: useLibsStore().getCanvas,
			downloadAsPdf: useLibsStore().downloadAsPdf,
			downloadAsJpeg: useLibsStore().downloadAsJpeg,
			getDateTimeString: () => {
				return moment().format('YYYY-MM-DD-hhmmss')
			},
			locale: locale,
			hash: hash,
		},
		temp: useTempStore(),
		bpmn: useBpmnStore(),
		formatters: useFormattersStore(),
		auth: useAuthStore(),
		profile: useAuthStore().profile,
		can: useAuthStore().can,
		quicklink: {
			uuid: null,
			handle() {
				const paramUuid = route.value?.params?.uuid
				if (paramUuid) {
					this.uuid = paramUuid
				}
			},
			has() {
				return Boolean(this.uuid)
			},
			reset(routerName) {
				this.uuid = null

				if (routerName) {
					router.push({ name: routerName }).catch(() => {})
				} else if (router.currentRoute?.meta?.quicklink) {
					const targetRoute = router.currentRoute.meta.quicklink
					router.push({ name: targetRoute }).catch(() => {})
				}
			},
		},
		options: useOptionsStore(),
		broadcasting: createEventBus(),
		events: {
			ticket_changed: 'ticket.changed',
			tickets_requests_refresh: 'tickets.requests.refresh',

			control_created: 'control.created',
			control_changed: 'control.changed',
			control_instance_changed: 'control.instance.changed',
			control_walkthrough_changed: 'control.walkthrough.changed',
			control_assessment_created: 'control.assessment.created',
			control_assessment_changed: 'control.assessment.changed',

			risk_created: 'risk.created',
			risk_changed: 'risk.changed',
			risk_instance_changed: 'risk.instance.changed',

			cycle_created: 'cycle.created',
			cycle_changed: 'cycle.changed',

			esg_materialities_created: 'esg.materialities.created',
			esg_materialities_changed: 'esg.materialities.changed',
			esg_materialities_deleted: 'esg.materialities.deleted',
			esg_validation_created: 'esg.validation.created',
			esg_validation_changed: 'esg.validation.changed',
			esg_assessment_created: 'esg.assessment.created',
			esg_assessment_changed: 'esg.assessment.changed',
			esg_data_changed: 'esg.data.changed',

			esg_calculator_created: 'esg.calculator.created',
			esg_calculator_changed: 'esg.calculator.changed',
			esg_calculator_stats_changed: 'esg.calculator.stats.changed',

			user_created: 'user.created',
			user_changed: 'user.changed',

			tasks_start: 'tasks.start',

			activity_clicked: 'activity.clicked',

			cyb_assets_created: 'cyb.assets.created',
			cyb_assets_changed: 'cyb.assets.changed',
			cyb_assets_deleted: 'cyb.assets.deleted',

			cyb_asset_instance_created: 'cyb.asset_instances.created',
			cyb_asset_instance_changed: 'cyb.asset_instances.changed',
			cyb_asset_instance_deleted: 'cyb.asset_instances.deleted',

			cyb_controls_created: 'cyb.controls.created',
			cyb_controls_changed: 'cyb.controls.changed',
			cyb_controls_deleted: 'cyb.controls.deleted',

			cyb_heatmap_changed: 'cyb.heatmap.changed',

			entity_created: 'entity.created',
			entity_changed: 'entity.changed',

			process_created: 'process.created',
			process_changed: 'process.changed',
			process_diagram_created: 'process.diagram.created',
			process_diagram_changed: 'process.diagram.changed',

			remediation_created: 'remediation.created',
			remediation_changed: 'remediation.changed',

			ipe_report_changed: 'ipe.report.changed',
		},
	})
}

let rysqer = Rysqer()

provide('rysqer', rysqer)

defineExpose({ rysqer })
</script>
